<script setup lang="tsx">
import type { Json } from '@/supabase/types';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { roleChipLabel, useRoles } from '@/src/core/admin/roles';
import { useAppState } from '@/src/store/ocg-app-state';

import { supabase } from '../../supabase';
import { getMessage } from '../../utils/exception';

const emit = defineEmits<{
  error: [message: string];
}>();

const appState = useAppState();

const dialog = ref(false);
const dialogValid = ref<boolean | null>(null);
const editedIndex = ref<number | null>(null);

const dialogDelete = ref(false);

interface Row {
  user: string;

  user_email: string;

  user_card: { [key: string]: Json | undefined };
  role: string;

  role_name: string;

  role_description: string;
  org: string;

  org_card: { [key: string]: Json | undefined };
}

interface Data {
  id: string | null;
  name: string;
  email: string;
  roles: string[];
}

const defaultItem = ref<Data>({
  id: null,
  name: '',
  email: '',
  roles: [],
});
const editedItem = ref<Data>();

const dialogTitle = computed(() => {
  return editedIndex.value === null ? 'New User' : 'Edit User';
});

const busy = ref(true);

const itemsPerPageOptions = [5, -1];

const { roles: roleItems, loadRoles, roleAutocompleteItems } = useRoles();

const items = ref<Row[]>([]);

const headers = ref([
  {
    title: 'Name',
    align: 'start' as const,
    sortable: true,
    key: 'user_card.fn',
  },
  { title: 'Email', key: 'user_email' },
  {
    title: 'Organization',
    align: 'start' as const,
    sortable: true,
    key: 'org_card.fn',
  },
  { title: 'Role', key: 'role_description' },
  { title: 'Actions', key: 'actions', sortable: false },
]);

const rules = {
  required: (value: string) => !!value || 'Field is required',
  email: (value: string) =>
    /.[^\n\r@\u2028\u2029]*@.+\..{2,}/.test(value)
    || 'Please enter an email address',
};

async function loadUsers() {
  const { data, error } = await supabase.from('user_role_details').select();
  if (error) throw error;
  items.value = data as Row[];
}

// TODO should get this type from the TS definition
interface PromiseRejectedResult {
  status: 'rejected';
  reason: any;
}

async function load() {
  busy.value = true;
  const results = await Promise.allSettled([loadUsers(), loadRoles()]);
  busy.value = false;
  const errors = results
    .filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )
    .map(result => getMessage(result.reason))
    .join('\n');
  if (errors) {
    emit('error', errors);
  }
}

defineExpose({ load });

onMounted(load);

function editedItemFromRow(item: Row): Data {
  let name = '';
  if (item.user_card && typeof item.user_card === 'object') {
    name = String(item.user_card.fn ?? '');
  }
  const email = item.user_email ?? '';
  const roles = items.value
    .filter(other => other.user === item.user)
    .map(other => other.role)
    .filter(role => !!role);
  return {
    id: item.user,
    name,
    email,
    roles,
  };
}

function editItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialog.value = true;
}

function resetEditedItem() {
  editedIndex.value = null;
  editedItem.value = Object.assign({} as Data, defaultItem.value);
}

watch(
  dialog,
  (value) => {
    if (!value) {
      nextTick(resetEditedItem);
    }
  },
  { immediate: true },
);

watch(dialogDelete, (value) => {
  if (!value) {
    nextTick(resetEditedItem);
  }
});

async function storeEditedItem() {
  if (!editedItem.value) return;

  try {
    if (editedItem.value.id !== null && editedIndex.value !== null) {
      // update
      const { error } = await supabase
        .from('person')
        .update({
          card: {
            ...items.value[editedIndex.value].user_card,
            fn: editedItem.value.name,
          },
        })
        .eq('id', editedItem.value.id);
      if (error) throw error;
    }
    else {
      // create
      const { data, error } = await supabase.rpc('create_user', {
        email: editedItem.value.email,
        name: editedItem.value.name,
      });
      if (error) throw error;
      editedItem.value.id = data;
    }
    const { error } = await supabase.rpc('assign_roles', {
      user: editedItem.value.id,
      roles: editedItem.value.roles,
    });
    if (error) throw error;
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
    throw error;
  }
  finally {
    load();
  }
}

async function save() {
  if (!dialogValid.value) return;
  try {
    await storeEditedItem();
    dialog.value = false;
    dialogValid.value = null;
  }
  catch {
    // already notified
  }
}

function deleteItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialogDelete.value = true;
}

async function deleteItemConfirm() {
  if (!editedItem.value?.id) return;
  if (appState.currentUserMode === 'superadmin') {
    try {
      const { error } = await supabase.rpc('remove_user', {
        user_id: editedItem.value.id,
      });
      if (error) throw error;
      load();
    }
    catch (error) {
      console.error(error);
      emit('error', getMessage(error));
    }
    finally {
      dialogDelete.value = false;
    }
    return;
  }
  // as normal org admin: remove roles only
  editedItem.value.roles = [];
  try {
    await storeEditedItem();
    dialogDelete.value = false;
  }
  catch {
    // already notified
  }
}
</script>

<template>
  <v-data-table
    items-per-page="5"
    :items-per-page-options="itemsPerPageOptions"
    :items="items"
    :headers="headers"
    :loading="busy"
  >
    <template #top>
      <v-toolbar flat>
        <v-dialog v-model="dialog" max-width="50em">
          <template #activator="{ props }">
            <v-btn color="primary" v-bind="props" prepend-icon="mdi-plus">
              Add
            </v-btn>
            <v-spacer />
          </template>
          <v-card>
            <v-card-title>
              <span class="text-headline-small">{{ dialogTitle }}</span>
            </v-card-title>

            <v-form
              v-model="dialogValid"
              validate-on="invalid-input"
              @submit.prevent="save"
            >
              <v-card-text>
                <v-container v-if="editedItem">
                  <v-row>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="[rules.required]"
                        label="Full Name"
                      />
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <div
                        v-tooltip="{
                          disabled: editedIndex === null,
                          text: 'Email cannot be changed',
                        }"
                      >
                        <v-text-field
                          v-model="editedItem.email"
                          label="Email"
                          type="email"
                          :rules="[rules.email]"
                          :disabled="editedIndex !== null"
                        />
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="editedItem.roles"
                        chips
                        label="Roles"
                        :items="roleAutocompleteItems"
                        multiple
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="dialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  type="submit"
                  :disabled="dialogValid === false"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogDelete"
          max-width="50em"
          @keydown.esc="dialogDelete = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Confirmation required
            </v-card-title>
            <v-card-text v-if="editedItem">
              <v-container class="d-flex flex-column">
                <v-row class="text-title-large text-center">
                  <v-col cols="12">
                    Really remove user
                    <span class="text-primary">{{ editedItem.name }}</span>?
                  </v-col>
                </v-row>
                <template v-if="editedItem.roles.length > 0">
                  <v-row>
                    <v-col cols="12">
                      The user will be removed from the following roles:
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" class="text-center">
                      <v-chip
                        v-for="role in editedItem.roles"
                        :key="role"
                        class="me-2"
                      >
                        {{
                          roleChipLabel(roleItems.find((r) => r.id === role))
                        }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </template>
                <v-row v-else>
                  <v-col cols="12">
                    The user has no roles.
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    This action <em>cannot</em> be undone!
                    <span
                      v-html="
                        appState.currentUserMode === 'superadmin'
                          ? 'The user will be <em>permanently deleted</em>.'
                          : 'The user account itself will <em>not</em> be deleted and may stay member of other organizations.'
                      "
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          :disabled="busy"
          icon="mdi-refresh"
          aria-label="Refresh"
          color="primary"
          @click="load"
        />
      </v-toolbar>
    </template>
    <template #item.actions="{ item }">
      <v-icon class="me-2" size="small" @click="editItem(item)">
        mdi-pencil
      </v-icon>
      <v-icon size="small" @click="deleteItem(item)">
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<style scoped></style>
